'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { startOfWeek } from 'date-fns/startOfWeek';
import { differenceInDays } from 'date-fns/differenceInDays';
import { addDays, parse } from 'date-fns';
import { cn } from '@/app/lib/utils';
import { SimpleDate, GameType, dateToSimpleDate, getStartAndEndOfWeek } from '@puzzlio/core';
import { GAME_CONFIG } from '@/app/games/config';
import { CompleteStarIcon, IncompleteDotsIcon } from '@puzzlio/ui';
import { useWeekStatusesForGame } from '@/app/hooks/gameBoardAndPlayHooks';
import { DayPlayStatus } from '@/app/types/game';

export type DayState = 'unavailable' | 'complete' | 'incomplete' | 'available';

export const DAY_LABELS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const playStatusToDayState = (status: DayPlayStatus): DayState => {
  if (status === 'complete') return 'complete';
  if (status === 'incomplete') return 'incomplete';
  return 'available';
};

const DayButton = ({
  day,
  state,
  selected,
  href,
}: {
  day: string;
  state: DayState;
  selected: boolean;
  href: string;
}) => {
  const className = cn(
    'group relative flex size-[38px] cursor-pointer items-center justify-center bg-white',
    state === 'available' &&
      cn(
        'hover:bg-yellow-t2 hover:text-yellow-s1 active:bg-yellow-t1 active:text-yellow-s2 text-black',
        selected && 'bg-gray-2'
      ),
    state === 'unavailable' && 'text-gray-2 cursor-default',
    state === 'complete' && cn('hover:bg-yellow-t2 active:bg-yellow-t1 text-white', selected && 'bg-gray-2 text-black'),
    state === 'incomplete' &&
      cn(
        'hover:bg-yellow-t2 hover:text-yellow-s1 active:bg-yellow-t1 active:text-yellow-s2 text-black',
        selected && 'bg-gray-2'
      )
  );
  const content = (
    <>
      {state === 'complete' && (
        <div className="absolute inset-0 flex items-center">
          <CompleteStarIcon
            className={cn(
              'group-hover:fill-yellow-s1 group-active:fill-yellow-s2 fill-black',
              selected && 'fill-white'
            )}
          />
        </div>
      )}
      {state === 'incomplete' && (
        <div className="absolute inset-x-[13%] bottom-[15%]">
          <IncompleteDotsIcon className="group-hover:fill-yellow-s1 group-active:fill-yellow-s2 fill-black" />
        </div>
      )}
      <span className="font-quicksand z-10">{day}</span>
    </>
  );

  if (state === 'unavailable') return <div className={className}>{content}</div>;
  return (
    <Link href={href} prefetch={false} className={className}>
      {content}
    </Link>
  );
};

interface WeekBarProps {
  selectedDate: SimpleDate;
  gameType: GameType;
  className?: string;
  withBorder?: boolean;
  // When set, render every day greyed-out and non-clickable without fetching week play data
  // (used during intro mode, where the week's puzzles aren't available to the player yet).
  disabled?: boolean;
}

/**
 * The seven days of `selectedDate`'s week, each with its play state. Null until mounted, since "today"
 * has to come from the client's timezone. With `disabled`, every day is unavailable and nothing is fetched.
 */
export function useWeekDays(
  gameType: GameType,
  selectedDate: SimpleDate,
  disabled = false
): { date: SimpleDate; state: DayState }[] | null {
  const [today, setToday] = useState<Date | null>(null);

  useEffect(() => {
    // Done client-side so it uses the client's timezone.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setToday(new Date());
  }, []);

  const [weekStart, weekEnd] = getStartAndEndOfWeek(selectedDate);
  // Every bar on the page shares this query key, so three bars still make one request — no need for a
  // parent to fetch on their behalf and pass the statuses down.
  const { data } = useWeekStatusesForGame(gameType, weekStart, weekEnd, { enabled: !disabled });

  if (!today) return null;

  const boardDate = parse(selectedDate, 'yyyy-MM-dd', today);
  const monday = startOfWeek(boardDate, { weekStartsOn: 1 });

  const week: { date: SimpleDate; state: DayState }[] = DAY_LABELS.map((_, idx) => {
    const dayDate = addDays(monday, idx);
    const state: DayState = disabled || dayDate > today ? 'unavailable' : 'available';
    return { date: dateToSimpleDate(dayDate), state };
  });

  if (data && !disabled) {
    data.forEach(({ date, playStatus }) => {
      const entryDate = parse(date, 'yyyy-MM-dd', today);
      if (entryDate > today) return;
      const dayIndex = differenceInDays(entryDate, monday);
      if (dayIndex < 0 || dayIndex > 6) return;
      week[dayIndex] = { date, state: playStatusToDayState(playStatus) };
    });
  }

  return week;
}

export const WeekBar = ({ className, selectedDate, gameType, withBorder = true, disabled = false }: WeekBarProps) => {
  const { gameURL } = GAME_CONFIG[gameType];
  const week = useWeekDays(gameType, selectedDate, disabled);
  if (!week) return null;

  return (
    <div className={cn('max-w-max rounded-lg bg-[#fcfbfa]', withBorder && 'border-2 border-black text-sm', className)}>
      <div className={cn('flex h-full w-full gap-1 pr-1 pl-3', withBorder && 'overflow-hidden rounded-lg')}>
        {week.map(({ date, state }, idx) => (
          <DayButton
            key={DAY_LABELS[idx]}
            day={DAY_LABELS[idx]}
            state={state}
            selected={date === selectedDate}
            href={gameURL(date)}
          />
        ))}
      </div>
    </div>
  );
};
